// components/chat-surface/chat-surface.js — THE REUSABLE CHAT SURFACE
// ═══════════════════════════════════════════════════════════
// Drop-in chat interface. Every feature that needs a chat should
// use this component and configure via props. Never rebuild from scratch.
//
// What's baked in (never reimplement):
//   - SSE streaming via useChatThread
//   - Scroll engine with auto-scroll + scroll-to-bottom
//   - Input bar with upload, deep search, deep mode, voice, send/stop
//   - Slash command picker (customizable command list)
//   - Paste stripping
//   - Message rendering with kindle/default/compact reading modes
//   - AI toolbar (copy, pin, regenerate) with Shift logo animation
//   - User toolbar (edit, copy, delete)
//   - Choice chips
//   - Canvas cards (updated/building/suggestion)
//   - Collapse long messages with "Show more"
//   - Thin blended scrollbar
//   - Streaming indicator (animated dots)
//   - Empty state
//
// What the feature controls via props:
//   - buildRequest: how to build the API call (the ONLY feature-specific surface)
//   - beforeSend: pre-send processing (file upload, etc.)
//   - onResponseDone: post-response side effects (artifact detect, etc.)
//   - headerComponent: custom header above the messages
//   - enableX flags: toggle features on/off
//   - slashCommands: custom command list
//   - readingMode: 'default' | 'kindle' | 'compact'
//   - placeholder: input placeholder text
//
// Usage:
//   <ChatSurface
//     userId={userId}
//     supabase={supabase}
//     sessionId={workspaceId}
//     userRecord={userRecord}
//     buildRequest={async ({ text, fileData, messagesSnapshot }) => ({
//       url: '/api/chat',
//       body: { messages: messagesSnapshot, userId, sessionId },
//     })}
//     onResponseDone={(text, metadata) => { /* feature logic */ }}
//     enableCanvasCards
//     readingMode="kindle"
//   />
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';
import { useChatThread } from '../../hooks/use-chat-thread';
import { useVoice } from '../../hooks/use-voice';
import ChatScrollEngine from './chat-scroll-engine';
import ChatMessageList from './chat-message-list';
import ChatInputEngine from './chat-input-engine';
import { ChatChips, CanvasSuggestionCard, CanvasCardStyles } from './chat-canvas-cards';

export default function ChatSurface({
  // ── Required ──
  userId,
  supabase,
  sessionId,
  userRecord,

  // ── Chat thread config (required) ──
  buildRequest,         // async ({ text, fileData, messagesSnapshot, isDeepSearch, isDeepMode }) => ({ url, method, headers, body })
  beforeSend,           // async () => ({ fileData?, userMsgExtras? }) — pre-send processing
  onResponseDone,       // async (finalText, userMsg, metadata) => void

  // ── Messages (controlled or uncontrolled) ──
  // Controlled: parent passes messages + setMessages (e.g. artifact/innovate tabs share state with parent)
  // Uncontrolled: ChatSurface owns messages internally, optionally loads via loadMessages
  messages: externalMessages,
  setMessages: externalSetMessages,
  initialMessages,      // array — used as starting state when uncontrolled
  loadMessages,         // async () => messages[] — called on mount to load history (uncontrolled only)

  // ── Hook config ──
  typingAnimation = false,

  // ── Feature flags ──
  enableUpload = true,
  enableDeepSearch = true,
  enableDeepMode = true,
  enableVoice = true,
  enableSlashCommands = true,
  enableCanvasCards = false,
  enableChips = true,

  // ── Customization ──
  readingMode = 'default',    // 'default' | 'kindle' | 'compact'
  placeholder = 'Message…',
  slashCommands,              // custom command list (defaults to built-in)
  onSlashCommand,             // custom command handler
  headerComponent,            // JSX to render above messages
  emptyStateComponent,        // JSX for empty state (no messages)
  filesAtLimit = false,

  // ── Callbacks ──
  onPin,                      // (message) => void
  onEdit,                     // (message) => void
  onDelete,                   // (message) => void

  // ── Voice config ──
  voiceConfig,                // { session, threadHistory, voiceEnabled, onVoiceToggle }
}) {
  // Controlled vs uncontrolled messages
  const [internalMessages, internalSetMessages] = useState(initialMessages || []);
  const messages = externalMessages || internalMessages;
  const setMessages = externalSetMessages || internalSetMessages;
  const [input, setInput] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [chips, setChips] = useState([]);
  const [canvasSuggestion, setCanvasSuggestion] = useState(null);
  const [deepSearchActive, setDeepSearchActive] = useState(false);
  const [deepModeArmed, setDeepModeArmed] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const inputRef = useRef(null);

  // ── Load messages on mount (uncontrolled mode only) ──
  useEffect(() => {
    if (loadMessages && !externalMessages) {
      let cancelled = false;
      setLoadingHistory(true);
      loadMessages().then(msgs => {
        if (!cancelled && msgs) setMessages(msgs);
      }).catch(e => {
        console.error('[ChatSurface] loadMessages failed:', e.message);
      }).finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
      return () => { cancelled = true; };
    }
  }, [sessionId]);

  // ── Chat thread hook ──
  const chat = useChatThread({
    messages,
    setMessages,
    typingAnimation,
    buildRequest: buildRequest || (async () => ({ url: '/api/chat', body: {} })),
    beforeSend: beforeSend || (async () => ({})),
    onResponseDone: async (finalText, userMsg, metadata) => {
      // Chips
      if (enableChips && metadata?.chips?.length > 0) {
        setChips(metadata.chips);
      } else {
        setChips([]);
      }
      // Canvas suggestion
      if (enableCanvasCards && metadata?.canvasSuggestion) {
        setCanvasSuggestion(metadata.canvasSuggestion);
      }
      // Feature-specific handler
      if (onResponseDone) {
        await onResponseDone(finalText, userMsg, metadata);
      }
    },
  });

  const { scrollContainerRef, lastUserMsgRef, spacerRef, loading, showScrollBtn, scrollToBottom, send, abort } = chat;

  // ── Voice ──
  const voiceResponseCallbackRef = useRef(null);
  const voice = useVoice({
    session: voiceConfig?.session || null,
    userRecord,
    currentSessionId: sessionId,
    threadHistory: voiceConfig?.threadHistory || [],
    voiceEnabled: voiceConfig?.voiceEnabled ?? enableVoice,
    setInput: (text) => {
      setInput(text);
      if (inputRef.current) inputRef.current.innerText = text;
    },
    submitMessage: (text) => submitMessage(text),
    setResponseCallback: (cb) => { voiceResponseCallbackRef.current = cb; },
  });

  const voiceListening = voice?.listening || false;
  const voiceEnabled = voice?.enabled ?? enableVoice;
  const toggleVoice = voice?.toggle;

  // ── Submit message ──
  const submitMessage = useCallback((directText) => {
    const text = directText || input;
    if (loading) return;
    if (!text?.trim() && !pendingFile) return;
    setInput('');
    setChips([]);
    setCanvasSuggestion(null);
    if (inputRef.current) inputRef.current.innerText = '';
    send(text, {
      isDeepSearch: deepSearchActive,
      isDeepMode: deepModeArmed,
    });
    if (deepSearchActive) setDeepSearchActive(false);
    if (deepModeArmed) setDeepModeArmed(false);
  }, [input, loading, pendingFile, send, deepSearchActive, deepModeArmed]);

  // ── File handling ──
  const handleFileSelect = useCallback((fileData) => {
    setPendingFile(fileData);
  }, []);

  // ── Regenerate ──
  const handleRegenerate = useCallback((message) => {
    if (loading || !message) return;
    const idx = messages.findIndex(m => m.id === message.id);
    if (idx < 0) return;
    if (message.role === 'assistant' && idx > 0) {
      const userMsg = messages[idx - 1];
      if (userMsg?.role === 'user') {
        setMessages(prev => prev.slice(0, idx));
        send(typeof userMsg.content === 'string' ? userMsg.content : '');
      }
    }
  }, [loading, messages, send, setMessages]);

  // ── Edit message ──
  const handleEdit = useCallback((message) => {
    if (message?.role !== 'user') return;
    const idx = messages.findIndex(m => m.id === message.id);
    if (idx < 0) return;
    setInput(typeof message.content === 'string' ? message.content : '');
    if (inputRef.current) inputRef.current.innerText = typeof message.content === 'string' ? message.content : '';
    setMessages(prev => prev.slice(0, idx));
    inputRef.current?.focus();
  }, [messages, setMessages]);

  // ── Empty state ──
  if (!loadingHistory && messages.length === 0 && emptyStateComponent) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#07070a' }}>
        {headerComponent}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {emptyStateComponent}
        </div>
        <ChatInputEngine
          inputRef={inputRef}
          value={input}
          setValue={setInput}
          onSend={submitMessage}
          loading={loading}
          onAbort={abort}
          pendingFile={pendingFile}
          setPendingFile={setPendingFile}
          onFileSelect={enableUpload ? handleFileSelect : undefined}
          filesAtLimit={filesAtLimit}
          deepSearchActive={deepSearchActive}
          setDeepSearchActive={enableDeepSearch ? setDeepSearchActive : undefined}
          deepModeArmed={deepModeArmed}
          setDeepModeArmed={enableDeepMode ? setDeepModeArmed : undefined}
          voiceListening={voiceListening}
          voiceEnabled={voiceEnabled}
          onVoiceToggle={enableVoice ? () => {
            const next = !voiceEnabled;
            voiceConfig?.onVoiceToggle?.(next);
            if (toggleVoice) toggleVoice();
          } : undefined}
          enableSlashCommands={enableSlashCommands}
          slashCommands={slashCommands}
          onSlashCommand={onSlashCommand}
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#07070a', overflow: 'hidden' }}>
      <CanvasCardStyles />

      {/* Optional header */}
      {headerComponent}

      {/* Messages + scroll engine */}
      <ChatScrollEngine
        scrollContainerRef={scrollContainerRef}
        lastUserMsgRef={lastUserMsgRef}
        spacerRef={spacerRef}
        showScrollBtn={showScrollBtn && messages.length > 0}
        onScrollToBottom={scrollToBottom}
      >
        {loadingHistory && (
          <div style={{
            textAlign: 'center', padding: 40, fontSize: 11,
            color: '#555', fontFamily: 'Georgia, serif', fontStyle: 'italic',
          }}>
            Loading...
          </div>
        )}

        <ChatMessageList
          messages={messages}
          lastUserMsgRef={lastUserMsgRef}
          userRecord={userRecord}
          readingMode={readingMode}
          onPin={onPin}
          onRegenerate={handleRegenerate}
          onEdit={handleEdit}
          onDelete={onDelete}
        />

        {/* Choice chips */}
        {enableChips && (
          <ChatChips
            chips={chips}
            loading={loading}
            onSelect={(chip) => { setChips([]); submitMessage(chip); }}
          />
        )}

        {/* Canvas suggestion */}
        {enableCanvasCards && canvasSuggestion && !loading && (
          <CanvasSuggestionCard
            suggestion={canvasSuggestion}
            aiName={userRecord?.ai_name}
            onApprove={() => {
              setCanvasSuggestion(null);
              submitMessage(`Yes, go ahead: ${canvasSuggestion}`);
            }}
            onDismiss={() => setCanvasSuggestion(null)}
          />
        )}
      </ChatScrollEngine>

      {/* Input engine */}
      <ChatInputEngine
        inputRef={inputRef}
        value={input}
        setValue={setInput}
        onSend={submitMessage}
        loading={loading}
        onAbort={abort}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        onFileSelect={enableUpload ? handleFileSelect : undefined}
        filesAtLimit={filesAtLimit}
        deepSearchActive={deepSearchActive}
        setDeepSearchActive={enableDeepSearch ? setDeepSearchActive : undefined}
        deepModeArmed={deepModeArmed}
        setDeepModeArmed={enableDeepMode ? setDeepModeArmed : undefined}
        voiceListening={voiceListening}
        voiceEnabled={voiceEnabled}
        onVoiceToggle={enableVoice ? () => {
          const next = !voiceEnabled;
          voiceConfig?.onVoiceToggle?.(next);
          if (toggleVoice) toggleVoice();
        } : undefined}
        enableSlashCommands={enableSlashCommands}
        slashCommands={slashCommands}
        onSlashCommand={onSlashCommand}
        placeholder={placeholder}
      />
    </div>
  );
}
